/**
 * EditLinkForm Component
 * Inline form for editing link title, URL, and tags
 */

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Effect } from "effect";
import { toast } from "sonner";
import type { UpdateLinkRequest } from "@link-storage/contracts";
import type { Link } from "../types/link";
import { updateLinkTitle } from "../services/linkService";
import { updateLinkTags } from "../services/tagService";
import { updateLinkSchema, type UpdateLinkInput } from "../schemas/link";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { TagInput } from "./TagInput";

interface EditLinkFormProps {
  link: Link;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function EditLinkForm({ link, onSuccess, onCancel }: EditLinkFormProps) {
  const form = useForm<UpdateLinkInput>({
    resolver: zodResolver(updateLinkSchema),
    defaultValues: {
      url: link.url,
      title: link.title || "",
      tags: link.tags?.map((t) => t.name) || [],
    },
  });

  const isSubmitting = form.formState.isSubmitting;

  const onSubmit = async (data: UpdateLinkInput) => {
    const request: UpdateLinkRequest = {
      title: data.title.trim(),
    };

    // Update title
    const titleProgram = updateLinkTitle(link.id, request).pipe(
      Effect.tapError((error) =>
        Effect.sync(() => {
          const message =
            error._tag === "NotFoundError"
              ? "링크를 찾을 수 없습니다"
              : error._tag === "ValidationError"
                ? error.message
                : "수정 중 오류가 발생했습니다";
          toast.error("수정 실패", { description: message });
        })
      ),
      Effect.catchAll(() => Effect.void)
    );

    // Update tags if changed
    const currentTags = link.tags?.map((t) => t.name) || [];
    const newTags = data.tags || [];
    const tagsChanged =
      currentTags.length !== newTags.length ||
      !currentTags.every((t) => newTags.includes(t));

    const tagsProgram = tagsChanged
      ? updateLinkTags(link.id, newTags).pipe(
          Effect.tapError((error) =>
            Effect.sync(() => {
              const message =
                error._tag === "NotFoundError"
                  ? "링크를 찾을 수 없습니다"
                  : "태그 수정 중 오류가 발생했습니다";
              toast.error("태그 수정 실패", { description: message });
            })
          ),
          Effect.catchAll(() => Effect.void)
        )
      : Effect.void;

    // Run both updates
    const program = Effect.all([titleProgram, tagsProgram]).pipe(
      Effect.tap(() =>
        Effect.sync(() => {
          toast.success("링크가 수정되었습니다");
          onSuccess?.();
        })
      )
    );

    await Effect.runPromise(program);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>제목</FormLabel>
              <FormControl>
                <Input
                  placeholder="링크 제목"
                  disabled={isSubmitting}
                  autoFocus
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>URL</FormLabel>
              <FormControl>
                <Input disabled {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="tags"
          render={({ field }) => (
            <FormItem>
              <FormLabel>태그</FormLabel>
              <FormControl>
                <TagInput
                  value={field.value || []}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            취소
          </Button>
          <Button type="submit" size="sm" disabled={isSubmitting}>
            {isSubmitting ? "저장 중..." : "저장"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
